import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

export interface OrgSyncLogDialogData {
  title: string;
  status: string | null;
  phase: string | null;
  errorMessage: string | null;
  logTail: string | null;
  /** Full on-disk trace (identity_sync_run_{id}.log) when deploy supports it. */
  fileLogTail: string | null;
  logDigest: string | null;
  identityTypesId: number;
  syncTiming?: {
    keycloak_ms: number | null;
    total_ms: number | null;
    after_keycloak_ms: number | null;
    lines: string[];
    summary: string;
  } | null;
}

@Component({
  selector: 'app-org-sync-log-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Seneste sync-log</h2>
    <mat-dialog-content class="log-dialog-content">
      <p><strong>Status:</strong> {{ data.status ?? '–' }}</p>
      <p><strong>Fase:</strong> {{ data.phase ?? '–' }}</p>
      @if (data.errorMessage) {
        <div class="error-block">
          <strong>Fejl / diagnose</strong>
          <pre class="error-text">{{ data.errorMessage }}</pre>
        </div>
      }
      @if (data.logDigest) {
        <div class="digest-block">
          <strong>Oversigt</strong>
          <pre class="digest-text">{{ data.logDigest }}</pre>
        </div>
      }
      @if (data.syncTiming?.lines?.length) {
        <div class="timing-block">
          <strong>Tidsforbrug (RCMS-server)</strong>
          <ul class="timing-list">
            @for (line of data.syncTiming!.lines; track line) {
              <li>{{ line }}</li>
            }
          </ul>
        </div>
        <p class="timing-hint muted">
          Keycloak-linjen er HTTP mod C3NG. «Hele sync» er hele org-kørslen; «Efter Keycloak» er resten — sammenlign
          med Keycloak-log via server start/slut på første linje.
        </p>
      }
      @if (data.fileLogTail) {
        <details class="log-details" open>
          <summary>Fil-log (server — alle trin)</summary>
          <p class="timing-hint muted">
            Skrives til logs/identity_sync_run_*.log på RCMS-serveren. Viser Trace, SIGNAL og SCRIPT KILLED selv
            når DB-log er afkortet.
          </p>
          <pre class="log-tail file-log">{{ data.fileLogTail }}</pre>
        </details>
      }
      <details class="log-details">
        <summary>Teknisk log (DB, rå)</summary>
        <pre class="log-tail">{{ data.logTail || 'Ingen log tilgængelig for denne kørsel.' }}</pre>
      </details>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Luk</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .log-dialog-content {
        min-width: 320px;
      }
      .log-tail {
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 50vh;
        overflow: auto;
        font-size: 12px;
        background: #f5f5f5;
        padding: 12px;
        border-radius: 4px;
      }
      .error-block {
        color: #b71c1c;
        background: #ffebee;
        padding: 10px 12px;
        border-radius: 4px;
        margin-bottom: 12px;
      }
      .error-text {
        white-space: pre-wrap;
        margin: 8px 0 0;
        font-size: 13px;
        line-height: 1.45;
        font-family: inherit;
        color: #b71c1c;
      }
      .digest-block {
        background: #fff8e1;
        padding: 10px 12px;
        border-radius: 4px;
        margin: 0 0 12px;
      }
      .digest-text {
        white-space: pre-wrap;
        margin: 8px 0 0;
        font-size: 13px;
        line-height: 1.45;
        font-family: inherit;
      }
      .log-details {
        margin-top: 8px;
      }
      .log-details summary {
        cursor: pointer;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.65);
        margin-bottom: 8px;
      }
      .timing-block {
        background: #e3f2fd;
        padding: 10px 12px;
        border-radius: 4px;
        margin: 0 0 8px;
      }
      .timing-list {
        margin: 8px 0 0;
        padding-left: 1.2rem;
      }
      .timing-list li {
        margin-bottom: 4px;
      }
      .timing-hint {
        font-size: 12px;
        margin: 0 0 12px;
      }
      .muted {
        color: rgba(0, 0, 0, 0.6);
      }
    `,
  ],
})
export class OrgSyncLogDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<OrgSyncLogDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrgSyncLogDialogData
  ) {}
}
